import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, Search, Plus, Trash2, Edit, Save, X, Tag, FileText, User, Calendar, Loader2, AlertCircle } from 'lucide-react';

const Wiki = () => {
    const [articles, setArticles] = useState([]);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Búsqueda y Filtros
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTag, setSelectedTag] = useState('');
    
    // Editor Form State
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editTags, setEditTags] = useState('');
    const [saveLoading, setSaveLoading] = useState(false);

    // Información de sesión para permisos de edición
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        // Decodificar usuario desde token
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                setCurrentUser(payload);
            } catch (e) {
                console.error(e);
            }
        }
        fetchArticles();
    }, [selectedTag]);

    const fetchArticles = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const projectId = localStorage.getItem('activeProjectId');
            const params = {};
            if (selectedTag) params.tag = selectedTag;
            
            const res = await axios.get('http://localhost:4000/api/wiki', {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'x-project-id': projectId
                },
                params
            });
            setArticles(res.data);
            
            // Si hay artículos y ninguno seleccionado, seleccionar el primero
            if (res.data.length > 0 && !selectedArticle) {
                setSelectedArticle(res.data[0]);
            }
        } catch (err) {
            console.error("Error fetching wiki articles:", err);
            setError("No se pudieron cargar los artículos de la base de conocimientos.");
        } finally {
            setLoading(false);
        }
    };

    // Filtrado en el cliente para búsqueda rápida
    const filteredArticles = articles.filter(art => {
        const matchesSearch = 
            art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            art.content.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    // Obtener todos los tags únicos para la barra lateral
    const allTags = [...new Set(articles.reduce((acc, art) => [...acc, ...(art.tags || [])], []))];

    const handleOpenCreate = () => {
        setEditId(null);
        setEditTitle('');
        setEditContent('');
        setEditTags('');
        setIsEditing(true);
    };

    const handleOpenEdit = (art) => {
        setEditId(art.id);
        setEditTitle(art.title);
        setEditContent(art.content);
        setEditTags((art.tags || []).join(', '));
        setIsEditing(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!editTitle.trim() || !editContent.trim()) return;

        setSaveLoading(true);
        try {
            const token = localStorage.getItem('token');
            const projectId = localStorage.getItem('activeProjectId');
            const tagsArray = editTags.split(',').map(t => t.trim()).filter(t => t.length > 0);

            let res;
            if (editId) {
                // Update
                res = await axios.put(`http://localhost:4000/api/wiki/${editId}`, {
                    title: editTitle,
                    content: editContent,
                    tags: tagsArray
                }, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'x-project-id': projectId
                    }
                });
                setArticles(prev => prev.map(a => a.id === editId ? res.data : a));
                setSelectedArticle(res.data);
            } else {
                // Create
                res = await axios.post('http://localhost:4000/api/wiki', {
                    title: editTitle,
                    content: editContent,
                    tags: tagsArray
                }, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'x-project-id': projectId
                    }
                });
                setArticles(prev => [res.data, ...prev]);
                setSelectedArticle(res.data);
            }
            setIsEditing(false);
        } catch (err) {
            console.error("Error saving article:", err);
            alert("Hubo un error al guardar el artículo.");
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de eliminar este artículo de la base de conocimientos?")) return;

        try {
            const token = localStorage.getItem('token');
            const projectId = localStorage.getItem('activeProjectId');
            await axios.delete(`http://localhost:4000/api/wiki/${id}`, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'x-project-id': projectId
                }
            });
            const remaining = articles.filter(a => a.id !== id);
            setArticles(remaining);
            if (selectedArticle && selectedArticle.id === id) {
                setSelectedArticle(remaining.length > 0 ? remaining[0] : null);
            }
        } catch (err) {
            console.error("Error deleting article:", err);
            alert("No se pudo eliminar el artículo.");
        }
    };

    // Verificar si el usuario actual es administrador o creador del artículo
    const canManage = currentUser && (currentUser.role === 'SUPER_ADMIN' || currentUser.role === 'COMPANY_ADMIN' || currentUser.role === 'PROJECT_MANAGER');

    return (
        <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(139,92,246,0.1),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans selection:bg-purple-500/30 animate-fade-in">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3">
                        <BookOpen size={36} className="text-purple-400" />
                        Centro de <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-500">Conocimiento y Manuales</span>
                    </h1>
                    <p className="text-theme-text-secondary font-medium">FAQs, guías técnicas y documentación operativa de la obra.</p>
                </div>
                {canManage && !isEditing && (
                    <button
                        onClick={handleOpenCreate}
                        className="px-5 py-3 bg-gradient-to-tr from-purple-600 to-indigo-500 hover:from-purple-500 hover:to-indigo-400 text-white rounded-2xl font-bold text-xs flex items-center gap-2 shadow-lg hover:scale-102 transition-all cursor-pointer border border-purple-500/20"
                    >
                        <Plus size={16} /> Crear Guía / Manual
                    </button>
                )}
            </div>

            {error && (
                <div className="max-w-7xl mx-auto bg-red-500/10 border border-red-500/20 text-red-450 p-4 rounded-2xl mb-6 flex items-center gap-3">
                    <AlertCircle size={20} /> {error}
                </div>
            )}

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* BARRA LATERAL (LISTA DE ARTÍCULOS Y FILTROS) */}
                <div className="lg:col-span-4 space-y-6">
                    {/* Buscador */}
                    <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-5 shadow-xl">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Buscar guías o FAQs..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-theme-bg border border-theme-border text-theme-text-primary text-xs rounded-2xl pl-10 pr-4 py-3 focus:outline-none focus:border-purple-500"
                            />
                            <Search size={16} className="absolute left-3.5 top-3.5 text-zinc-500" />
                        </div>
                    </div>

                    {/* Filtro por Tags */}
                    {allTags.length > 0 && (
                        <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-5 shadow-xl">
                            <h4 className="text-xs font-black uppercase text-theme-text-secondary tracking-widest mb-3">Filtrar por Etiqueta</h4>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setSelectedTag('')}
                                    className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all border cursor-pointer ${
                                        !selectedTag 
                                            ? 'bg-purple-600 border-purple-500 text-white shadow' 
                                            : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:text-theme-text-primary'
                                    }`}
                                >
                                    Todos
                                </button>
                                {allTags.map(tag => (
                                    <button
                                        key={tag}
                                        onClick={() => setSelectedTag(tag)}
                                        className={`px-3 py-1.5 rounded-xl text-[10px] font-bold transition-all border cursor-pointer flex items-center gap-1.5 ${
                                            selectedTag === tag 
                                                ? 'bg-purple-600 border-purple-500 text-white shadow' 
                                                : 'bg-theme-bg border-theme-border text-theme-text-secondary hover:text-theme-text-primary'
                                        }`}
                                    >
                                        <Tag size={8} /> {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Listado de Artículos */}
                    <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-5 shadow-xl space-y-3">
                        <h4 className="text-xs font-black uppercase text-theme-text-secondary tracking-widest mb-2">Artículos disponibles</h4>
                        
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-10 text-zinc-550 gap-2">
                                <Loader2 className="animate-spin text-purple-500" size={24} />
                                <span className="text-[10px] font-bold">Cargando artículos...</span>
                            </div>
                        ) : filteredArticles.length === 0 ? (
                            <div className="text-center py-10 text-theme-text-secondary text-xs">
                                No se encontraron guías que coincidan con la búsqueda.
                            </div>
                        ) : (
                            <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                                {filteredArticles.map(art => (
                                    <div
                                        key={art.id}
                                        onClick={() => { setSelectedArticle(art); setIsEditing(false); }}
                                        className={`p-3.5 rounded-2xl border transition-all cursor-pointer text-left ${
                                            selectedArticle && selectedArticle.id === art.id
                                                ? 'bg-purple-600/10 border-purple-500/30 shadow'
                                                : 'bg-theme-bg/40 border-theme-border hover:border-purple-500/20'
                                        }`}
                                    >
                                        <h5 className="text-xs font-black text-theme-text-primary line-clamp-1 mb-1.5">{art.title}</h5>
                                        <p className="text-[10px] text-theme-text-secondary line-clamp-2 leading-relaxed mb-2.5">
                                            {art.content}
                                        </p>
                                        <div className="flex justify-between items-center text-[8px] font-mono text-theme-text-secondary">
                                            <span className="flex items-center gap-1"><User size={8} /> {art.author_name || 'Admin'}</span>
                                            <span>{new Date(art.updated_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* VISTA DETALLE O EDITOR */}
                <div className="lg:col-span-8">
                    {/* MODO EDICIÓN / CREACIÓN */}
                    {isEditing ? (
                        <form onSubmit={handleSave} className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 md:p-8 shadow-xl space-y-6 animate-fade-in text-left">
                            <div className="flex justify-between items-center border-b border-theme-border pb-4">
                                <h3 className="text-lg font-bold text-theme-text-primary flex items-center gap-2">
                                    <FileText className="text-purple-400" size={20} />
                                    {editId ? 'Editar Artículo' : 'Nuevo Artículo de Consulta'}
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(false)}
                                    className="p-1.5 hover:bg-theme-border/20 rounded-xl text-slate-500 hover:text-theme-text-primary transition-colors cursor-pointer"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-theme-text-secondary tracking-wider mb-2">Título de la Guía o Pregunta</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="Ej: ¿Cómo configuro las alertas de equipo EPP?"
                                        value={editTitle}
                                        onChange={(e) => setEditTitle(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border text-theme-text-primary text-xs rounded-xl px-4 py-3 focus:outline-none focus:border-purple-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase text-theme-text-secondary tracking-wider mb-2">Etiquetas / Palabras Clave (Separadas por comas)</label>
                                    <input
                                        type="text"
                                        placeholder="cctv, camaras, dahua, config, ehs"
                                        value={editTags}
                                        onChange={(e) => setEditTags(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border text-theme-text-primary text-xs rounded-xl px-4 py-3 focus:outline-none focus:border-purple-500 font-mono"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black uppercase text-theme-text-secondary tracking-wider mb-2">Contenido de la Guía (Markdown o Texto Plano)</label>
                                    <textarea
                                        required
                                        rows={12}
                                        placeholder="Describe de forma clara el manual, comando, o solución técnica..."
                                        value={editContent}
                                        onChange={(e) => setEditContent(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border text-theme-text-primary text-xs rounded-xl p-4 focus:outline-none focus:border-purple-500 resize-none font-sans leading-relaxed"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 border-t border-theme-border pt-5">
                                <button
                                    type="button"
                                    onClick={() => setIsEditing(false)}
                                    className="flex-1 bg-theme-bg hover:bg-theme-border/20 border border-theme-border py-3 text-theme-text-secondary hover:text-theme-text-primary rounded-xl font-bold text-xs transition-all cursor-pointer"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saveLoading}
                                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-500 py-3 text-white rounded-xl font-bold text-xs shadow-lg hover:scale-[1.01] transition-all cursor-pointer flex items-center justify-center gap-2"
                                >
                                    {saveLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                    Guardar Artículo
                                </button>
                            </div>
                        </form>
                    ) : selectedArticle ? (
                        /* MODO DETALLE VISTA LECTURA */
                        <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-6 md:p-8 shadow-xl space-y-6 text-left animate-fade-in relative">
                            {/* Acciones del Artículo */}
                            {canManage && (
                                <div className="absolute right-6 top-6 flex gap-2">
                                    <button
                                        onClick={() => handleOpenEdit(selectedArticle)}
                                        className="p-2 bg-theme-bg border border-theme-border hover:border-purple-500/30 text-purple-400 rounded-xl transition-all cursor-pointer hover:scale-105"
                                        title="Editar Artículo"
                                    >
                                        <Edit size={14} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(selectedArticle.id)}
                                        className="p-2 bg-theme-bg border border-theme-border hover:border-red-500/30 text-red-400 rounded-xl transition-all cursor-pointer hover:scale-105"
                                        title="Eliminar Artículo"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            )}

                            <div>
                                <h2 className="text-2xl font-black text-theme-text-primary tracking-tight leading-snug mb-3 pr-20">{selectedArticle.title}</h2>
                                <div className="flex flex-wrap gap-4 text-xs text-theme-text-secondary font-mono border-b border-theme-border/50 pb-4">
                                    <span className="flex items-center gap-1.5"><User size={12} /> Publicado por: <span className="font-bold text-purple-400">{selectedArticle.author_name || 'Administrador'}</span></span>
                                    <span className="flex items-center gap-1.5"><Calendar size={12} /> Actualizado: {new Date(selectedArticle.updated_at).toLocaleString()}</span>
                                </div>
                            </div>

                            {/* Contenido Formateado */}
                            <div className="text-sm text-theme-text-primary leading-relaxed whitespace-pre-wrap font-sans font-medium space-y-4">
                                {selectedArticle.content}
                            </div>

                            {/* Tags de Pie */}
                            {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                                <div className="border-t border-theme-border/50 pt-4 flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-theme-text-secondary mr-1">Etiquetas:</span>
                                    {selectedArticle.tags.map(tag => (
                                        <span key={tag} className="bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[9px] font-bold px-2 py-0.5 rounded-lg flex items-center gap-1">
                                            <Tag size={6} /> {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* SIN ARTÍCULOS SELECCIONADOS */
                        <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-3xl p-16 text-center shadow-xl flex flex-col items-center justify-center min-h-[400px]">
                            <BookOpen size={64} className="text-purple-500/20 mb-4 animate-pulse" />
                            <h3 className="text-xl font-bold text-theme-text-primary">Base de Conocimiento Vacía</h3>
                            <p className="text-xs text-theme-text-secondary max-w-sm mt-2">
                                Aún no hay artículos creados en este proyecto. Utiliza el botón "Crear Guía / Manual" para agregar contenido.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Wiki;
